import { useState, ReactNode, useEffect } from 'react';
import { ErrorPage } from '../components/ErrorPage/ErrorPage';
import { BlankLoadingPage } from '../views/BlankLoadingPage/BlankLoadingPage';
import ConfigContext from './ConfigContext';
import { StorefrontConfigError, StorefrontConfigResponse } from 'config-types';
import { applySemanticThemeTokens, tailwindcssPaletteGenerator, setFavicon } from '../helpers';

interface IConfigProviderProps {
    children?: ReactNode;
}

const ConfigProvider = ({
    children
}: IConfigProviderProps) => {
    const localStorageStorefrontConfig = localStorage.getItem('shopConfig');
    const [shopConfig, setStorefrontConfig] = useState<StorefrontConfigResponse | null>(
        localStorageStorefrontConfig != null ? (JSON.parse(localStorageStorefrontConfig) as StorefrontConfigResponse) : null
    );
    const [error, setError] = useState<StorefrontConfigError | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(!localStorageStorefrontConfig);

    const redirectToCustomDomain = (config: StorefrontConfigResponse) => {
        // Check if we have a custom domain and we're not already on it
        if (config.customDomain?.domainName) {
            const currentHost = window.location.host;
            const customDomain = config.customDomain.domainName;
            
            // Only redirect if we're not already on the custom domain
            if (currentHost !== customDomain) {
                console.log(`Redirecting to custom domain: ${customDomain}`);
                
                // Create the new URL using the current protocol, path, and search parameters
                const newUrl = `${window.location.protocol}//${customDomain}${window.location.pathname}${window.location.search}`;
                
                // Redirect to the custom domain
                window.location.href = newUrl;
                
                // Return true to indicate that a redirect is happening
                return true;
            }
        }
        return false;
    };

    const refreshStorefrontConfig = () => {
        console.log("refreshStorefrontConfig");
        setError(null);
        setIsLoading(true);
        const configUrl = import.meta.env.VITE_APP_CONFIG_API_ENDPOINT + '/get-config';
        fetch(configUrl, {
            headers: {
                'X-MarketplacePro-Host': window.location.host
            }
        })
            .then((response) => response.json())
            .then((shopConfigResponse: StorefrontConfigResponse) => {
                // Try to redirect if a custom domain is found
                const isRedirecting = redirectToCustomDomain(shopConfigResponse);
                
                // Only update state if we're not redirecting
                if (!isRedirecting) {
                    setStorefrontConfig(shopConfigResponse);
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                console.error('Config fetch error:', error);
                setError({ message: error.message });
                setIsLoading(false);
            });
    };

    const applyThemeSettings = () => {
        if (shopConfig?.theme?.faviconUrl) {
            setFavicon(shopConfig.theme.faviconUrl);
        }
        // Semantic tokens drive the design system (bg-primary etc.); the
        // legacy --app-* shade scale still feeds the few remaining numeric
        // shade utilities (primary-500/900).
        applySemanticThemeTokens(shopConfig?.theme);
        if (shopConfig?.theme?.primaryColor) {
            tailwindcssPaletteGenerator("primary", shopConfig.theme.primaryColor);
        }

        if (shopConfig?.theme?.secondaryColor) {
            tailwindcssPaletteGenerator("secondary", shopConfig.theme.secondaryColor);
        }
    }

    useEffect(() => {
            // update localStorage
            shopConfig != null
                ? localStorage.setItem('shopConfig', JSON.stringify(shopConfig))
                : sessionStorage.removeItem('shopConfig');

            applyThemeSettings();
    }, [shopConfig]);

    useEffect(() => {
            refreshStorefrontConfig();
    }, []);

    if (error) return (<ErrorPage message={error.message} />);
    if (isLoading || !shopConfig) return (<BlankLoadingPage />);

    return (
        <ConfigContext.Provider
            value={{
                shopConfig
            }}
        >
            {children}
        </ConfigContext.Provider>
    );
};

export default ConfigProvider;
